import { useState, useEffect, useCallback } from "react";
import styles from "./AdminUsers.module.css";
import { getAdminPerms } from "../../../../lib/adminPerms";
import {
  adminListUsers,
  adminUpdateUser,
  adminDeleteUser,
  getSpecialties,
  adminListAdmins,
  adminCreateAdmin,
  adminUpdateAdmin,
  adminDeleteAdmin,
  adminListBookingRecipients,
  adminAddBookingRecipient,
  adminRemoveBookingRecipient,
} from "../../../../lib/store";

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

const PER_PAGE = 10;

const ROLES = ["Member", "Manager", "Partner"];

// Admin-panel modules (NOT the staging dashboard modules). Each module
// declares only the actions that apply to it; the rest render as "—".
const ADMIN_MODULES = [
  { key: "documents",   label: "Resources",       actions: ["view", "add"] },
  { key: "assessments", label: "Assessments",     actions: ["view"] },
  { key: "tickets",     label: "Tickets",         actions: ["view"] },
  { key: "bookings",    label: "Bookings",        actions: ["view"] },
  { key: "team",        label: "User Management", actions: ["view", "add", "edit"] },
];

const PERM_KEYS = ["view", "add", "edit", "delete"];

// Build a full permissions object over ADMIN_MODULES keys (all four action
// booleans), seeded from an existing module_permissions record (or empty).
function seedPerms(src) {
  const source = src && typeof src === "object" ? src : {};
  const out = {};
  ADMIN_MODULES.forEach((m) => {
    const cur = source[m.key] || {};
    out[m.key] = {
      view: cur.view === true,
      add: cur.add === true,
      edit: cur.edit === true,
      delete: cur.delete === true,
    };
  });
  return out;
}

// One "Module Access" row — module name + View/Add/Edit/Delete checkboxes.
// Checking Add/Edit/Delete auto-checks View; unchecking View clears the rest.
function PermissionRow({ label, actions, perm, onChange }) {
  const p = perm || {};
  const supported = actions || PERM_KEYS;

  const toggle = (key) => {
    if (!supported.includes(key)) return;
    const next = {
      view: !!p.view,
      add: !!p.add,
      edit: !!p.edit,
      delete: !!p.delete,
    };
    next[key] = !next[key];
    if (key !== "view" && next[key]) next.view = true;
    if (key === "view" && !next.view) {
      next.add = false;
      next.edit = false;
      next.delete = false;
    }
    onChange(next);
  };

  return (
    <div className={`${styles.tmPermRow} ${p.view ? styles.tmPermRowActive : ""}`}>
      <div className={styles.tmPermRowText}>
        <span className={styles.tmPermissionLabel}>{label}</span>
      </div>
      <div className={styles.tmPermCheckGroup}>
        {PERM_KEYS.map((key) => {
          const isSupported = supported.includes(key);
          return (
            <label key={key} className={styles.tmPermCell}>
              {isSupported ? (
                <input
                  type="checkbox"
                  className={styles.tmPermissionCheckbox}
                  checked={!!p[key]}
                  onChange={() => toggle(key)}
                  aria-label={`${key} ${label}`}
                />
              ) : (
                <span className={styles.tmPermNA}>—</span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function PermTableHeader() {
  return (
    <div className={`${styles.tmPermRow} ${styles.tmPermHeader}`}>
      <div className={styles.tmPermRowText} />
      <div className={styles.tmPermCheckGroup}>
        <span className={styles.tmPermCell}>View</span>
        <span className={styles.tmPermCell}>Add</span>
        <span className={styles.tmPermCell}>Edit</span>
        <span className={styles.tmPermCell}>Delete</span>
      </div>
    </div>
  );
}

// Staging-styled modal frame: title, subtitle, close (×), scrollable body,
// sticky footer.
function ModalShell({ title, subtitle, onClose, children, footer }) {
  return (
    <div className={styles.tmOverlay} role="presentation">
      <div className={styles.tmModal} role="dialog" aria-modal="true">
        <button
          type="button"
          className={styles.tmCloseBtn}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        {(title || subtitle) && (
          <div className={styles.tmModalHero}>
            {title && <h3 className={styles.tmModalTitle}>{title}</h3>}
            {subtitle && <p className={styles.tmModalSubtitle}>{subtitle}</p>}
          </div>
        )}
        <div className={styles.tmModalBody}>{children}</div>
        {footer && <div className={styles.tmModalFooter}>{footer}</div>}
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const perms = getAdminPerms("team");
  // Only the top-level superadmin (role "Admin") manages the Internal Team.
  // Members created by the admin never see the Internal Team tab.
  const isSuperAdmin = (() => {
    try {
      return (
        (JSON.parse(sessionStorage.getItem("user") || "{}").role_type || "") ===
        "Admin"
      );
    } catch {
      return false;
    }
  })();
  const [tab, setTab] = useState("members"); // "members" | "internal" | "recipients"

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  // Internal Team (backend admins) state.
  const [internalAdmins, setInternalAdmins] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const [internalError, setInternalError] = useState("");
  const [internalQuery, setInternalQuery] = useState("");
  const [showInternalAdd, setShowInternalAdd] = useState(false);
  const [internalEditing, setInternalEditing] = useState(null);
  const [internalRemove, setInternalRemove] = useState(null);
  const [flash, setFlash] = useState("");

  // Doctor Recipients (calBookingEmails) state.
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [recipientsError, setRecipientsError] = useState("");
  const [recipientsQuery, setRecipientsQuery] = useState("");
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [removeRecipient, setRemoveRecipient] = useState(null);

  // Auto-dismiss the success banner after a few seconds.
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(""), 3000);
    return () => clearTimeout(t);
  }, [flash]);

  // The logged-in admin should never see their own account in the list.
  const currentEmail = (() => {
    try {
      return (JSON.parse(sessionStorage.getItem("user") || "{}").email || "")
        .toLowerCase();
    } catch {
      return "";
    }
  })();

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    adminListUsers()
      .then(setUsers)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadInternal = useCallback(() => {
    setInternalLoading(true);
    setInternalError("");
    adminListAdmins()
      .then(setInternalAdmins)
      .catch((e) => setInternalError(e.message))
      .finally(() => setInternalLoading(false));
  }, []);

  useEffect(() => {
    loadInternal();
  }, [loadInternal]);

  const loadRecipients = useCallback(() => {
    setRecipientsLoading(true);
    setRecipientsError("");
    adminListBookingRecipients()
      .then(setRecipients)
      .catch((e) => setRecipientsError(e.message))
      .finally(() => setRecipientsLoading(false));
  }, []);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  // Hide the admin's own account, then apply the search filter.
  const visibleUsers = users.filter(
    (u) => !(currentEmail && (u.email || "").toLowerCase() === currentEmail),
  );
  const filtered = visibleUsers.filter((u) => {
    if (statusFilter !== "all" && u.account_status !== statusFilter) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [u.firstname, u.lastname, u.email, u.specialty].some((v) =>
      (v || "").toLowerCase().includes(q),
    );
  });

  // Reset to first page when the search query or status filter changes.
  useEffect(() => {
    setPage(1);
  }, [query, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageUsers = filtered.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE,
  );

  // Internal team: hide the logged-in admin's own account, then search filter.
  const adminEmail = getAdminEmail().toLowerCase();
  const internalVisible = internalAdmins.filter(
    (a) => !(adminEmail && (a.email || "").toLowerCase() === adminEmail),
  );
  const internalFiltered = internalVisible.filter((a) => {
    const q = internalQuery.trim().toLowerCase();
    if (!q) return true;
    const name = `${a.firstname || ""} ${a.lastname || ""}`.trim();
    return [name, a.email].some((v) => (v || "").toLowerCase().includes(q));
  });

  // Filtered recipients list for search
  const filteredRecipients = recipients.filter((r) => {
    const q = recipientsQuery.trim().toLowerCase();
    if (!q) return true;
    return [
      r.name,
      r.email,
    ].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className={styles.wrap}>
      <div className={styles.headerRow}>
        <div>
          <div className={styles.pageTitle}>User Management</div>
          <div className={styles.subtle}>
            {tab === "members"
              ? `${visibleUsers.length} users`
              : tab === "internal"
              ? `${internalVisible.length} internal team members`
              : `${recipients.length} booking email recipients`}
          </div>
        </div>
        {tab === "internal" && perms.canAdd && (
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => setShowInternalAdd(true)}
          >
            + Add Team
          </button>
        )}
        {tab === "recipients" && (
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => setShowAddRecipient(true)}
          >
            Add +
          </button>
        )}
      </div>

      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tabBtn} ${tab === "members" ? styles.tabBtnActive : ""}`}
          onClick={() => setTab("members")}
        >
          Users ({visibleUsers.length})
        </button>
        {isSuperAdmin && (
          <button
            type="button"
            className={`${styles.tabBtn} ${tab === "internal" ? styles.tabBtnActive : ""}`}
            onClick={() => setTab("internal")}
          >
            Internal Team ({internalVisible.length})
          </button>
        )}
        <button
          type="button"
          className={`${styles.tabBtn} ${tab === "recipients" ? styles.tabBtnActive : ""}`}
          onClick={() => setTab("recipients")}
        >
          Add Doctor Recipient ({recipients.length})
        </button>
      </div>

      {flash && <div className={styles.success}>{flash}</div>}

      {tab === "members" && (
      <>
      <div className={styles.filterRow}>
        <input
          className={`${styles.search} ${styles.searchInRow}`}
          placeholder="Search users..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className={styles.statusSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="Approved">Approved</option>
          <option value="Pending">Pending</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className={styles.spinnerWrap}>
          <span className={styles.spinner} />
          <span>Loading users…</span>
        </div>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : filtered.length === 0 ? (
        <div className={styles.empty}>No users found.</div>
      ) : (
        <>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.idxCol}>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Specialty</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageUsers.map((u, i) => (
                <tr key={u.id}>
                  <td className={styles.idxCol}>{(currentPage - 1) * PER_PAGE + i + 1}</td>
                  <td>{`${u.firstname} ${u.lastname}`.trim() || "—"}</td>
                  <td>{u.email}</td>
                  <td>{u.specialty || "—"}</td>
                  <td>{u.role_type || "—"}</td>
                  <td>
                    <span
                      className={`${styles.tag} ${
                        styles["st_" + (u.account_status || "none").toLowerCase()]
                      }`}
                    >
                      {u.account_status || "—"}
                    </span>
                  </td>
                  <td className={styles.actionsCell}>
                    {perms.canEdit && (
                      <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.iconEdit}`}
                        onClick={() => setEditing(u)}
                        title="Edit"
                        aria-label={`Edit ${u.email}`}
                      >
                        <EditIcon />
                      </button>
                    )}
                    {perms.canDelete && (
                      <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.iconDanger}`}
                        onClick={() => setConfirmRemove(u)}
                        title="Delete"
                        aria-label={`Delete ${u.email}`}
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageBtn}
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span className={styles.pageInfo}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className={styles.pageBtn}
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next →
            </button>
          </div>
        )}
        </>
      )}
      </>
      )}

      {tab === "internal" && (
      <>
      <div className={styles.filterRow}>
        <input
          className={`${styles.search} ${styles.searchInRow}`}
          placeholder="Search internal team..."
          value={internalQuery}
          onChange={(e) => setInternalQuery(e.target.value)}
        />
      </div>

      {internalLoading ? (
        <div className={styles.spinnerWrap}>
          <span className={styles.spinner} />
          <span>Loading internal team…</span>
        </div>
      ) : internalError ? (
        <div className={styles.error}>{internalError}</div>
      ) : internalFiltered.length === 0 ? (
        <div className={styles.empty}>No internal team members found.</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.idxCol}>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {internalFiltered.map((a, i) => (
                <tr key={a.email}>
                  <td className={styles.idxCol}>{i + 1}</td>
                  <td>
                    {`${a.firstname || ""} ${a.lastname || ""}`.trim() || a.email}
                  </td>
                  <td>{a.email}</td>
                  <td>{a.role || "—"}</td>
                  <td className={styles.actionsCell}>
                    {perms.canEdit && (
                      <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.iconEdit}`}
                        onClick={() => setInternalEditing(a)}
                        title="Edit"
                        aria-label={`Edit ${a.email}`}
                      >
                        <EditIcon />
                      </button>
                    )}
                    {perms.canDelete && (
                      <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.iconDanger}`}
                        onClick={() => setInternalRemove(a)}
                        title="Delete"
                        aria-label={`Delete ${a.email}`}
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {tab === "recipients" && (
      <>
        <div className={styles.filterRow}>
          <input
            className={`${styles.search} ${styles.searchInRow}`}
            placeholder="Search recipients..."
            value={recipientsQuery}
            onChange={(e) => setRecipientsQuery(e.target.value)}
          />
        </div>

        {recipientsLoading ? (
          <div className={styles.spinnerWrap}>
            <span className={styles.spinner} />
            <span>Loading recipients…</span>
          </div>
        ) : recipientsError ? (
          <div className={styles.error}>{recipientsError}</div>
        ) : filteredRecipients.length === 0 ? (
          <div className={styles.empty}>
            {recipients.length === 0
              ? <span>No recipients yet. Click <strong>+ Add Doctor Recipient</strong> to add one.</span>
              : "No recipients match your search."}
          </div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.idxCol}>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecipients.map((r, i) => (
                  <tr key={r.email}>
                    <td className={styles.idxCol}>{i + 1}</td>
                    <td>{r.name}</td>
                    <td>{r.email}</td>
                    <td className={styles.actionsCell}>
                      <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.iconDanger}`}
                        onClick={() => setRemoveRecipient(r)}
                        title="Remove"
                        aria-label={`Remove ${r.email}`}
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
      )}

      {showAddRecipient && (
        <AddRecipientModal
          onClose={() => setShowAddRecipient(false)}
          onSaved={() => {
            setShowAddRecipient(false);
            loadRecipients();
            setFlash("Doctor recipient added successfully.");
          }}
        />
      )}

      {removeRecipient && (
        <RemoveRecipientConfirm
          recipient={removeRecipient}
          onClose={() => setRemoveRecipient(null)}
          onRemoved={() => {
            setRemoveRecipient(null);
            loadRecipients();
            setFlash("Doctor recipient removed.");
          }}
        />
      )}

      {showInternalAdd && (
        <InternalAddModal
          onClose={() => setShowInternalAdd(false)}
          onSaved={() => {
            setShowInternalAdd(false);
            loadInternal();
            setFlash("User created successfully.");
          }}
        />
      )}

      {internalEditing && (
        <InternalEditModal
          admin={internalEditing}
          onClose={() => setInternalEditing(null)}
          onSaved={() => {
            setInternalEditing(null);
            loadInternal();
            setFlash("User updated successfully.");
          }}
        />
      )}

      {internalRemove && (
        <InternalRemoveConfirm
          admin={internalRemove}
          onClose={() => setInternalRemove(null)}
          onRemoved={() => {
            setInternalRemove(null);
            loadInternal();
            setFlash("User removed successfully.");
          }}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(props) => {
            setUsers((prev) =>
              prev.map((x) => (x.id === editing.id ? { ...x, ...props } : x)),
            );
            setEditing(null);
          }}
        />
      )}

      {confirmRemove && (
        <RemoveConfirm
          user={confirmRemove}
          onClose={() => setConfirmRemove(null)}
          onRemoved={() => {
            setUsers((prev) => prev.filter((x) => x.id !== confirmRemove.id));
            setConfirmRemove(null);
          }}
        />
      )}
    </div>
  );
}

function RemoveConfirm({ user, onClose, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const confirm = async () => {
    setBusy(true);
    setErr("");
    try {
      await adminDeleteUser({ id: user.id });
      onRemoved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const name = `${user.firstname} ${user.lastname}`.trim() || user.email;

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Delete User</div>
        {err && <div className={styles.error}>{err}</div>}
        <div className={styles.subtle} style={{ fontSize: "0.9rem" }}>
          Are you sure you want to delete <strong>{name}</strong> ({user.email})?
          This archives the contact in HubSpot.
        </div>
        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.dangerBtn}
            onClick={confirm}
            disabled={busy}
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [firstname, setFirstname] = useState(user.firstname || "");
  const [lastname, setLastname] = useState(user.lastname || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [specialty, setSpecialty] = useState(user.specialty || "");
  const [role, setRole] = useState(user.role_type || "Member");
  const [accountStatus, setAccountStatus] = useState(user.account_status || "");
  // Per-module permissions (view/add/edit/delete), seeded from the user record.
  const [perms, setPerms] = useState(() => seedPerms(user.module_permissions));
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    let mounted = true;
    getSpecialties()
      .then((data) => {
        if (mounted) setSpecialtyOptions(data?.specialties || []);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const setPermission = (key, next) =>
    setPerms((cur) => ({ ...cur, [key]: next }));

  const save = async () => {
    setErr("");
    const fe = {};
    if (!firstname.trim()) fe.firstname = "First name is required.";
    if (!lastname.trim()) fe.lastname = "Last name is required.";
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) return;
    setBusy(true);
    try {
      await adminUpdateUser({
        id: user.id,
        properties: {
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          phone: phone.trim(),
          specialty,
          role_type: role,
          account_status: accountStatus,
          // HubSpot stores module_permissions as a JSON string.
          module_permissions: JSON.stringify(perms),
        },
      });
      onSaved({
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        phone: phone.trim(),
        specialty,
        role_type: role,
        account_status: accountStatus,
        module_permissions: perms,
      });
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`Edit ${`${user.firstname || ""} ${user.lastname || ""}`.trim() || user.email}`}
      subtitle="Update details and adjust which admin modules this user can access."
      onClose={onClose}
      footer={
        <div className={styles.tmActions}>
          <button
            type="button"
            className={styles.tmCancelBtn}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.tmSubmitBtn}
            onClick={save}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      }
    >
      <div className={styles.tmForm}>
        <div className={styles.tmFormSection}>
          <div className={styles.tmSectionIntro}>
            <h4 className={styles.tmSectionTitle}>Basic Details</h4>
          </div>

          <div className={styles.tmRow2}>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>First Name *</label>
              <input
                type="text"
                className={styles.tmInput}
                value={firstname}
                onChange={(e) => {
                  setFirstname(e.target.value);
                  if (fieldErrors.firstname)
                    setFieldErrors((fe) => ({ ...fe, firstname: "" }));
                }}
                placeholder="Enter first name"
              />
              {fieldErrors.firstname && (
                <span className={styles.tmErr}>{fieldErrors.firstname}</span>
              )}
            </div>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>Last Name *</label>
              <input
                type="text"
                className={styles.tmInput}
                value={lastname}
                onChange={(e) => {
                  setLastname(e.target.value);
                  if (fieldErrors.lastname)
                    setFieldErrors((fe) => ({ ...fe, lastname: "" }));
                }}
                placeholder="Enter last name"
              />
              {fieldErrors.lastname && (
                <span className={styles.tmErr}>{fieldErrors.lastname}</span>
              )}
            </div>
          </div>

          <div className={styles.tmRow2}>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>Email</label>
              <input
                type="email"
                className={styles.tmInput}
                value={user.email || ""}
                disabled
              />
            </div>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>Phone (optional)</label>
              <input
                type="tel"
                className={styles.tmInput}
                value={phone}
                onChange={(e) => {
                  const filtered = e.target.value.replace(/[^\d+\-() ]/g, "");
                  const digitCount = filtered.replace(/\D/g, "").length;
                  if (digitCount > 12) return;
                  setPhone(filtered);
                }}
                placeholder="+1 (555) 123-4567"
                inputMode="tel"
              />
            </div>
          </div>
        </div>

        <div className={styles.tmFormSection}>
          <div className={styles.tmSectionIntro}>
            <h4 className={styles.tmSectionTitle}>Role &amp; Status</h4>
          </div>

          <div className={styles.tmRow2}>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>Role *</label>
              <select
                className={styles.tmSelect}
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {ROLES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>Status</label>
              <select
                className={styles.tmSelect}
                value={accountStatus}
                onChange={(e) => setAccountStatus(e.target.value)}
              >
                <option value="">—</option>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div className={styles.tmField}>
            <label className={styles.tmLabel}>Professional Role (optional)</label>
            <select
              className={styles.tmSelect}
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
            >
              <option value="">— Select role —</option>
              {specialtyOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.tmFormSection}>
          <div className={styles.tmSectionIntro}>
            <h4 className={styles.tmSectionTitle}>Module Access</h4>
            <p className={styles.tmSectionText}>
              Choose which admin modules this user can access and what they can
              do in each.
            </p>
          </div>
          <div className={styles.tmPermissionStack}>
            <PermTableHeader />
            {ADMIN_MODULES.map((module) => (
              <PermissionRow
                key={module.key}
                label={module.label}
                actions={module.actions}
                perm={perms[module.key]}
                onChange={(next) => setPermission(module.key, next)}
              />
            ))}
          </div>
        </div>

        {err && <span className={styles.tmErr}>{err}</span>}
      </div>
    </ModalShell>
  );
}

// Logged-in admin's email — new users are created under this account.
function getAdminEmail() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}").email || "";
  } catch {
    return "";
  }
}

// ── Internal Team: Add modal (backend admin login) ──
function InternalAddModal({ onClose, onSaved }) {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [perms, setPerms] = useState(() => seedPerms({}));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const setPermission = (key, next) =>
    setPerms((cur) => ({ ...cur, [key]: next }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const fe = {};
    if (!firstname.trim()) fe.firstname = "First name is required.";
    if (!lastname.trim()) fe.lastname = "Last name is required.";
    if (!email.trim()) fe.email = "Email is required.";
    if (!password) {
      fe.password = "Password is required.";
    } else if (password.length < 6) {
      fe.password = "Password must be at least 6 characters.";
    }
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) return;

    setBusy(true);
    try {
      await adminCreateAdmin({
        email: email.trim(),
        password,
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        module_permissions: perms,
      });
      onSaved();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title="Add Team"
      onClose={onClose}
      footer={
        <div className={styles.tmActions}>
          <button
            type="button"
            className={styles.tmCancelBtn}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="internalAddForm"
            className={styles.tmSubmitBtn}
            disabled={busy}
          >
            {busy ? "Adding…" : "Add Team"}
          </button>
        </div>
      }
    >
      <form id="internalAddForm" className={styles.tmForm} onSubmit={submit} noValidate>
        <div className={styles.tmFormSection}>
          <div className={styles.tmSectionIntro}>
            <h4 className={styles.tmSectionTitle}>Basic Details</h4>
          </div>

          <div className={styles.tmRow2}>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>First Name *</label>
              <input
                type="text"
                className={styles.tmInput}
                value={firstname}
                onChange={(e) => {
                  setFirstname(e.target.value);
                  if (fieldErrors.firstname)
                    setFieldErrors((f) => ({ ...f, firstname: "" }));
                }}
                placeholder="Enter first name"
              />
              {fieldErrors.firstname && (
                <span className={styles.tmErr}>{fieldErrors.firstname}</span>
              )}
            </div>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>Last Name *</label>
              <input
                type="text"
                className={styles.tmInput}
                value={lastname}
                onChange={(e) => {
                  setLastname(e.target.value);
                  if (fieldErrors.lastname)
                    setFieldErrors((f) => ({ ...f, lastname: "" }));
                }}
                placeholder="Enter last name"
              />
              {fieldErrors.lastname && (
                <span className={styles.tmErr}>{fieldErrors.lastname}</span>
              )}
            </div>
          </div>

          <div className={styles.tmField}>
            <label className={styles.tmLabel}>Email *</label>
            <input
              type="email"
              className={styles.tmInput}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldErrors.email)
                  setFieldErrors((f) => ({ ...f, email: "" }));
              }}
              placeholder="name@company.com"
            />
            {fieldErrors.email && (
              <span className={styles.tmErr}>{fieldErrors.email}</span>
            )}
          </div>

          <div className={styles.tmField}>
            <label className={styles.tmLabel}>Password *</label>
            <input
              type="text"
              className={styles.tmInput}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (fieldErrors.password)
                  setFieldErrors((f) => ({ ...f, password: "" }));
              }}
              placeholder="Min 6 characters"
            />
            {fieldErrors.password && (
              <span className={styles.tmErr}>{fieldErrors.password}</span>
            )}
          </div>
        </div>

        <div className={styles.tmFormSection}>
          <div className={styles.tmSectionIntro}>
            <h4 className={styles.tmSectionTitle}>Module Access</h4>
            <p className={styles.tmSectionText}>
              Choose which admin modules this user can access and what they can
              do in each.
            </p>
          </div>
          <div className={styles.tmPermissionStack}>
            <PermTableHeader />
            {ADMIN_MODULES.map((module) => (
              <PermissionRow
                key={module.key}
                label={module.label}
                actions={module.actions}
                perm={perms[module.key]}
                onChange={(next) => setPermission(module.key, next)}
              />
            ))}
          </div>
        </div>

        {err && <span className={styles.tmErr}>{err}</span>}
      </form>
    </ModalShell>
  );
}

// ── Internal Team: Edit modal (backend admin login) ──
function InternalEditModal({ admin, onClose, onSaved }) {
  const [firstname, setFirstname] = useState(admin.firstname || "");
  const [lastname, setLastname] = useState(admin.lastname || "");
  const [password, setPassword] = useState("");
  const [perms, setPerms] = useState(() => seedPerms(admin.module_permissions));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const setPermission = (key, next) =>
    setPerms((cur) => ({ ...cur, [key]: next }));

  const name =
    `${admin.firstname || ""} ${admin.lastname || ""}`.trim() || admin.email;

  const save = async () => {
    setErr("");
    const fe = {};
    if (!firstname.trim()) fe.firstname = "First name is required.";
    if (!lastname.trim()) fe.lastname = "Last name is required.";
    if (password && password.length < 6)
      fe.password = "Password must be at least 6 characters.";
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) return;

    setBusy(true);
    try {
      const payload = {
        email: admin.email,
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        module_permissions: perms,
      };
      if (password) payload.password = password;
      await adminUpdateAdmin(payload);
      onSaved();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`Edit ${name}`}
      subtitle="Update details and adjust which admin modules this user can access."
      onClose={onClose}
      footer={
        <div className={styles.tmActions}>
          <button
            type="button"
            className={styles.tmCancelBtn}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.tmSubmitBtn}
            onClick={save}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      }
    >
      <div className={styles.tmForm}>
        <div className={styles.tmFormSection}>
          <div className={styles.tmSectionIntro}>
            <h4 className={styles.tmSectionTitle}>Basic Details</h4>
          </div>

          <div className={styles.tmRow2}>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>First Name *</label>
              <input
                type="text"
                className={styles.tmInput}
                value={firstname}
                onChange={(e) => {
                  setFirstname(e.target.value);
                  if (fieldErrors.firstname)
                    setFieldErrors((f) => ({ ...f, firstname: "" }));
                }}
                placeholder="Enter first name"
              />
              {fieldErrors.firstname && (
                <span className={styles.tmErr}>{fieldErrors.firstname}</span>
              )}
            </div>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>Last Name *</label>
              <input
                type="text"
                className={styles.tmInput}
                value={lastname}
                onChange={(e) => {
                  setLastname(e.target.value);
                  if (fieldErrors.lastname)
                    setFieldErrors((f) => ({ ...f, lastname: "" }));
                }}
                placeholder="Enter last name"
              />
              {fieldErrors.lastname && (
                <span className={styles.tmErr}>{fieldErrors.lastname}</span>
              )}
            </div>
          </div>

          <div className={styles.tmRow2}>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>Email</label>
              <input
                type="email"
                className={styles.tmInput}
                value={admin.email || ""}
                disabled
              />
            </div>
            <div className={styles.tmField}>
              <label className={styles.tmLabel}>Password</label>
              <input
                type="text"
                className={styles.tmInput}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldErrors.password)
                    setFieldErrors((f) => ({ ...f, password: "" }));
                }}
                placeholder="Leave blank to keep current"
              />
              {fieldErrors.password && (
                <span className={styles.tmErr}>{fieldErrors.password}</span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.tmFormSection}>
          <div className={styles.tmSectionIntro}>
            <h4 className={styles.tmSectionTitle}>Module Access</h4>
            <p className={styles.tmSectionText}>
              Choose which admin modules this user can access and what they can
              do in each.
            </p>
          </div>
          <div className={styles.tmPermissionStack}>
            <PermTableHeader />
            {ADMIN_MODULES.map((module) => (
              <PermissionRow
                key={module.key}
                label={module.label}
                actions={module.actions}
                perm={perms[module.key]}
                onChange={(next) => setPermission(module.key, next)}
              />
            ))}
          </div>
        </div>

        {err && <span className={styles.tmErr}>{err}</span>}
      </div>
    </ModalShell>
  );
}

// ── Internal Team: Delete confirm ──
function InternalRemoveConfirm({ admin, onClose, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const name =
    `${admin.firstname || ""} ${admin.lastname || ""}`.trim() || admin.email;

  const confirm = async () => {
    setBusy(true);
    setErr("");
    try {
      await adminDeleteAdmin({ email: admin.email });
      onRemoved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Delete User</div>
        {err && <div className={styles.error}>{err}</div>}
        <div className={styles.subtle} style={{ fontSize: "0.9rem" }}>
          Are you sure you want to delete <strong>{name}</strong> ({admin.email})
          from the internal team? This removes their admin-panel login.
        </div>
        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.dangerBtn}
            onClick={confirm}
            disabled={busy}
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Cal Booking Emails: Add recipient modal ──
function AddRecipientModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const fe = {};
    if (!name.trim()) fe.name = "Name is required.";
    if (!email.trim()) fe.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      fe.email = "Enter a valid email address.";
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) return;

    setBusy(true);
    try {
      await adminAddBookingRecipient({ name: name.trim(), email: email.trim() });
      onSaved();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title="Add Doctor Recipient"
      subtitle="Set an email address to receive instant booking notifications whenever a user schedules an appointment."
      onClose={onClose}
      footer={
        <div className={styles.tmActions}>
          <button
            type="button"
            className={styles.tmCancelBtn}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="addRecipientForm"
            className={styles.tmSubmitBtn}
            disabled={busy}
          >
            {busy ? "Saving…" : "Add Recipient"}
          </button>
        </div>
      }
    >
      <form id="addRecipientForm" className={styles.tmForm} onSubmit={submit} noValidate>
        <div className={styles.tmFormSection}>
          <div className={styles.tmSectionIntro}>
            <h4 className={styles.tmSectionTitle}>Recipient Details</h4>
          </div>

          <div className={styles.tmField}>
            <label className={styles.tmLabel}>Full Name *</label>
            <input
              type="text"
              className={styles.tmInput}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (fieldErrors.name) setFieldErrors((f) => ({ ...f, name: "" }));
              }}
              placeholder="e.g. Dr. John Smith"
            />
            {fieldErrors.name && (
              <span className={styles.tmErr}>{fieldErrors.name}</span>
            )}
          </div>

          <div className={styles.tmField}>
            <label className={styles.tmLabel}>Email Address *</label>
            <input
              type="email"
              className={styles.tmInput}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldErrors.email) setFieldErrors((f) => ({ ...f, email: "" }));
              }}
              placeholder="doctor@clinic.com"
            />
            {fieldErrors.email && (
              <span className={styles.tmErr}>{fieldErrors.email}</span>
            )}
          </div>

          {err && <span className={styles.tmErr}>{err}</span>}
        </div>
      </form>
    </ModalShell>
  );
}

// ── Cal Booking Emails: Remove recipient confirm ──
function RemoveRecipientConfirm({ recipient, onClose, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const confirm = async () => {
    setBusy(true);
    setErr("");
    try {
      await adminRemoveBookingRecipient({ email: recipient.email });
      onRemoved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Remove Recipient</div>
        {err && <div className={styles.error}>{err}</div>}
        <div className={styles.subtle} style={{ fontSize: "0.9rem" }}>
          Remove <strong>{recipient.name}</strong> ({recipient.email}) from the
          Cal booking email list? They will no longer receive booking
          notifications.
        </div>
        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.dangerBtn}
            onClick={confirm}
            disabled={busy}
          >
            {busy ? "Removing…" : "Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}
